import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Layers, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await api.get("/admin/tiers");
      setTiers(response.data);
      // Initialize edit data
      const data = {};
      response.data.forEach(tier => {
        data[tier.id] = {
          threshold_points: tier.threshold_points,
          multiplier: tier.multiplier,
          benefits: tier.benefits
        };
      });
      setEditData(data);
    } catch (error) {
      toast.error("Failed to load tiers");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (tierId) => {
    const data = editData[tierId];
    if (!data) return;

    setSaving(tierId);
    try {
      await api.put(`/admin/tiers/${tierId}?threshold_points=${data.threshold_points}&multiplier=${data.multiplier}&benefits=${encodeURIComponent(data.benefits)}`);
      toast.success("Tier updated");
      fetchTiers();
    } catch (error) {
      toast.error("Failed to update tier");
    } finally {
      setSaving(null);
    }
  };

  const updateField = (tierId, field, value) => {
    setEditData(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        [field]: value
      }
    }));
  };

  const getTierColor = (name) => {
    switch(name?.toLowerCase()) {
      case 'gold': return 'from-[#D4AF37] to-[#B8942D]';
      case 'platinum': return 'from-[#E5E4E2] to-[#BDB5B5]';
      default: return 'from-[#C0C0C0] to-[#A8A8A8]';
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Tier Settings">
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-48 rounded-xl animate-shimmer" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tier Settings">
      <div className="space-y-6 animate-fade-in">
        <p className="text-[#A0A0A0]">Configure loyalty tier thresholds, multipliers, and benefits</p>

        {/* Tier Cards */}
        <div className="space-y-4">
          {tiers.map((tier) => (
            <Card key={tier.id} className="bg-[#131316] border-[#2A2A2F] overflow-hidden">
              <div className={`h-2 bg-gradient-to-r ${getTierColor(tier.name)}`} />
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${getTierColor(tier.name)} flex items-center justify-center`}>
                    <Star size={18} className="text-[#0A0A0B]" />
                  </div>
                  {tier.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A0A0A0]">Threshold (Miles)</Label>
                    <Input
                      type="number"
                      value={editData[tier.id]?.threshold_points ?? tier.threshold_points}
                      onChange={(e) => updateField(tier.id, 'threshold_points', parseInt(e.target.value) || 0)}
                      className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                      data-testid={`tier-threshold-${tier.id}`}
                    />
                    <p className="text-[#6B6B6B] text-xs">Minimum lifetime miles to reach this tier</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A0A0A0]">Points Multiplier</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editData[tier.id]?.multiplier ?? tier.multiplier}
                      onChange={(e) => updateField(tier.id, 'multiplier', parseFloat(e.target.value) || 1)}
                      className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                      data-testid={`tier-multiplier-${tier.id}`}
                    />
                    <p className="text-[#6B6B6B] text-xs">Bonus multiplier for earned points</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A0A0A0]">Benefits</Label>
                    <Input
                      value={editData[tier.id]?.benefits ?? tier.benefits}
                      onChange={(e) => updateField(tier.id, 'benefits', e.target.value)}
                      className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                      data-testid={`tier-benefits-${tier.id}`}
                    />
                    <p className="text-[#6B6B6B] text-xs">Description of tier benefits</p>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    onClick={() => handleSave(tier.id)}
                    disabled={saving === tier.id}
                    className="bg-[#D4AF37] text-[#0A0A0B]"
                    data-testid={`save-tier-${tier.id}`}
                  >
                    {saving === tier.id ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {tiers.length === 0 && (
          <div className="text-center py-16">
            <Layers size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Tiers Configured</h3>
            <p className="text-[#A0A0A0]">Default tiers will be created automatically</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTiers;
